import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Send, MessageCircle, User, Truck, Briefcase, Loader2, Search, Mail, Phone } from 'lucide-react';
import { supportAdminThreads, supportAdminThread, supportAdminReply } from '../services/api';
import { toast } from 'sonner';

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: '2-digit', month: 'short' });
};

const RoleIcon = ({ role, className = 'w-4 h-4' }) => {
  if (role === 'driver') return <Truck className={className} />;
  if (role === 'business') return <Briefcase className={className} />;
  return <User className={className} />;
};

export const AdminSupportInbox = () => {
  const [threads, setThreads] = useState([]);
  const [activeUserId, setActiveUserId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const listRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      setLoadingList(true);
      const res = await supportAdminThreads();
      setThreads(res.data.threads || []);
    } catch {
      /* silent */
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadActive = useCallback(async (uid) => {
    if (!uid) return;
    try {
      setLoadingThread(true);
      const res = await supportAdminThread(uid);
      setActiveThread(res.data);
      // Refresh thread list so the unread badge clears.
      loadThreads();
      setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 60);
    } catch {
      /* silent */
    } finally {
      setLoadingThread(false);
    }
  }, [loadThreads]);

  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 15000);
    return () => clearInterval(t);
  }, [loadThreads]);

  useEffect(() => {
    if (!activeUserId) return;
    loadActive(activeUserId);
    const t = setInterval(() => loadActive(activeUserId), 10000);
    return () => clearInterval(t);
  }, [activeUserId, loadActive]);

  const handleReply = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || !activeUserId || sending) return;
    setSending(true);
    try {
      await supportAdminReply(activeUserId, body);
      setDraft('');
      await loadActive(activeUserId);
      toast.success('Reply sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const filtered = threads.filter((t) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (t.user_name || '').toLowerCase().includes(q)
      || (t.user_email || '').toLowerCase().includes(q)
      || (t.last_message || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4" data-testid="admin-support-inbox">
      {/* Thread list */}
      <div className="bg-white border border-gray-200 rounded-lg flex flex-col h-[70vh] md:h-[75vh] min-h-[400px]">
        <div className="p-3 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-sm text-gray-900">Conversations <span className="font-normal text-gray-400 text-xs">({threads.length})</span></h3>
            {loadingList && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, message…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30"
              data-testid="support-thread-search"
            />
          </div>
        </div>
        <ul className="flex-1 overflow-y-auto divide-y divide-gray-100" data-testid="support-thread-list">
          {filtered.length === 0 && !loadingList && (
            <li className="p-6 text-center text-sm text-gray-400">No conversations yet.</li>
          )}
          {filtered.map((t) => (
            <li key={t.user_id}>
              <button
                type="button"
                onClick={() => setActiveUserId(t.user_id)}
                className={`w-full text-left p-3 hover:bg-gray-50 transition-colors flex gap-3 items-start ${activeUserId === t.user_id ? 'bg-[#2E7D32]/5 border-l-2 border-l-[#2E7D32]' : ''}`}
                data-testid={`support-thread-${t.user_id}`}
              >
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  {t.user_profile_image
                    ? <img src={t.user_profile_image} alt="" className="w-10 h-10 rounded-full object-cover" />
                    : <RoleIcon role={t.user_role} className="w-5 h-5 text-gray-600" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900 truncate">{t.user_name}</p>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{fmtTime(t.last_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 capitalize">{t.user_role}</span>
                  </div>
                  <p className={`text-xs mt-1 line-clamp-2 ${t.unread > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                    {t.last_from_admin && <span className="text-[10px] text-[#2E7D32] font-semibold mr-1">You:</span>}
                    {t.last_message}
                  </p>
                </div>
                {t.unread > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center flex-shrink-0 self-center" data-testid={`support-thread-unread-${t.user_id}`}>
                    {t.unread > 9 ? '9+' : t.unread}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Active thread */}
      <div className="bg-white border border-gray-200 rounded-lg flex flex-col h-[70vh] md:h-[75vh] min-h-[400px]">
        {!activeUserId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-6 text-center">
            <MessageCircle className="w-10 h-10 mb-2 text-gray-300" strokeWidth={1.5} />
            <p className="text-sm font-medium">Pick a conversation to reply</p>
            <p className="text-xs mt-1">All customer &amp; driver support messages will appear here.</p>
          </div>
        ) : (
          <>
            {/* Thread header */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                {activeThread?.user?.profile_image
                  ? <img src={activeThread.user.profile_image} alt="" className="w-10 h-10 rounded-full object-cover" />
                  : <RoleIcon role={activeThread?.user?.role} className="w-5 h-5 text-gray-600" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-gray-900 truncate" data-testid="support-thread-name">{activeThread?.user?.name || '—'}</p>
                <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
                  <span className="uppercase font-semibold tracking-wider">{activeThread?.user?.role || ''}</span>
                  {activeThread?.user?.email && (
                    <a href={`mailto:${activeThread.user.email}`} className="flex items-center gap-1 hover:text-[#2E7D32]"><Mail className="w-3 h-3" />{activeThread.user.email}</a>
                  )}
                  {activeThread?.user?.phone && (
                    <a href={`tel:${activeThread.user.phone}`} className="flex items-center gap-1 hover:text-[#2E7D32]"><Phone className="w-3 h-3" />{activeThread.user.phone}</a>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-gray-50 to-white" data-testid="support-thread-messages">
              {loadingThread && (!activeThread || !activeThread.messages?.length) && (
                <p className="text-center text-xs text-gray-400 py-6">Loading…</p>
              )}
              {(activeThread?.messages || []).map((m) => (
                <div key={m.id} className={`flex ${m.from_admin ? 'justify-end' : 'justify-start'}`} data-testid={`admin-msg-${m.id}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${
                    m.from_admin
                      ? 'bg-[#2E7D32] text-white rounded-br-sm'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                  }`}>
                    {m.body}
                    <div className={`mt-1 text-[10px] ${m.from_admin ? 'text-white/70' : 'text-gray-400'}`}>
                      {fmtTime(m.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Composer */}
            <form onSubmit={handleReply} className="border-t border-gray-200 p-3 bg-white flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(e); } }}
                placeholder="Type your reply…"
                rows={1}
                className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30 max-h-32"
                data-testid="admin-reply-input"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={!draft.trim() || sending}
                className="h-10 px-4 rounded-full bg-[#2E7D32] text-white flex items-center gap-2 disabled:opacity-40 hover:bg-[#1B5E20] transition-all"
                data-testid="admin-reply-send"
              >
                <Send className="w-4 h-4" /> Send
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSupportInbox;
